import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MAP_AREA_COLOR, MAP_AREA_EDITABLE_COLOR, MAP_AREA_FILL_OPACITY, MAP_AREA_STROKE_WEIGHT, } from '../constants/config';
import { applyAreaPointDrag, getAreaMidpoints } from '../utils/applyAreaPointDrag';
import { googleCoordinates } from '../utils/googleCoordinates';
import { toGeoCoordinates } from '../utils/toGeoCoordinates';
import AreaHandle from './AreaHandle';
/**
 * Draws one area as a Google Maps polygon. An editable area gets large handles: a corner can be dragged or tapped
 * (`onVertexClick`), and the dot in the middle of an edge can be dragged or tapped to insert a new corner there.
 * Every finished change is reported through `onChange` with all corners. A read-only `clickable` area reports a tap
 * on it through `onAreaClick` with its key.
 */
export default function AreaPolygon({ area, onChange, onVertexClick, onClick, onAreaClick }) {
    const map = useMap();
    const polygonRef = useRef(null);
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    const onAreaClickRef = useRef(onAreaClick);
    onAreaClickRef.current = onAreaClick;
    const areaRef = useRef(area);
    areaRef.current = area;
    const pointsRef = useRef(area.points);
    pointsRef.current = area.points;
    const [drag, setDrag] = useState(undefined);
    // Counts finished drags. It is part of every handle's key, so after each drag all handles are created anew at the
    // area's points: also when the change was refused and the points stayed, which would leave the dragged handle
    // where Google dropped it.
    const [dragCount, setDragCount] = useState(0);
    const color = area.color ?? (area.editable ? MAP_AREA_EDITABLE_COLOR : MAP_AREA_COLOR);
    const shownPoints = useMemo(() => applyAreaPointDrag(area.points, drag), [area.points, drag]);
    const midpoints = useMemo(() => (area.editable ? getAreaMidpoints(area.points) : []), [area.editable, area.points]);
    useEffect(() => {
        if (!map)
            return;
        const polygon = new google.maps.Polygon({ map });
        polygonRef.current = polygon;
        return () => {
            polygon.setMap(null);
            polygonRef.current = null;
        };
    }, [map]);
    useEffect(() => {
        const polygon = polygonRef.current;
        if (!polygon)
            return;
        polygon.setOptions({
            paths: [shownPoints.map(googleCoordinates)],
            clickable: Boolean(area.editable || area.clickable),
            strokeColor: color,
            strokeWeight: MAP_AREA_STROKE_WEIGHT,
            fillColor: color,
            fillOpacity: MAP_AREA_FILL_OPACITY,
        });
    }, [map, shownPoints, area.editable, area.clickable, color]);
    useEffect(() => {
        const polygon = polygonRef.current;
        if (!polygon)
            return;
        const listener = polygon.addListener('click', (event) => {
            const { key, editable } = areaRef.current;
            if (!editable) {
                onAreaClickRef.current?.(key);
                return;
            }
            // A tap inside the editable area is a tap on the map, not on the area.
            if (event.latLng)
                onClickRef.current?.(toGeoCoordinates(event.latLng));
        });
        return () => listener.remove();
    }, [map]);
    const finishDrag = (nextDrag) => {
        setDrag(undefined);
        setDragCount((count) => count + 1);
        onChange?.(area.key, applyAreaPointDrag(pointsRef.current, nextDrag));
    };
    if (!area.editable)
        return null;
    return (_jsxs(_Fragment, { children: [midpoints.map((midpoint) => (_jsx(AreaHandle
            // The position is in the key, so after every change a fresh handle replaces the one that was dragged.
            , { position: midpoint.position, color: color, variant: "midpoint", onDrag: (position) => setDrag({ index: midpoint.index, position, inserted: true }), onDragEnd: (position) => finishDrag({ index: midpoint.index, position, inserted: true }), onClick: () => finishDrag({ index: midpoint.index, position: midpoint.position, inserted: true }) }, `midpoint-${dragCount}-${midpoint.index}-${midpoint.position.latitude}-${midpoint.position.longitude}`))), area.points.map((point, index) => (_jsx(AreaHandle
            // As with the midpoints: a fresh handle replaces the dragged one, which Google keeps where it was dropped.
            , { position: point, color: color, variant: "vertex", onDrag: (position) => setDrag({ index, position, inserted: false }), onDragEnd: (position) => finishDrag({ index, position, inserted: false }), onClick: () => onVertexClick?.(area.key, index) }, `vertex-${dragCount}-${index}-${point.latitude}-${point.longitude}`)))] }));
}
